/**
 * Système de cache LRU pour les animations de lettres
 * Gère le préchargement, le cache par alphabet, et la gestion mémoire
 */

class AnimationCache {
  constructor() {
    // Cache par alphabet: Map<alphabetId, Map<lettre, VideoElement>>
    this.cache = new Map();
    
    // Cache de thumbnails par alphabet: Map<alphabetId, Map<lettre, thumbnailDataUrl>>
    this.thumbnailCache = new Map();
    
    // Lettres préchargées par alphabet: Map<alphabetId, Set<lettre>>
    this.preloadedLetters = new Map();
    
    // Compteurs d'utilisation pour LRU: Map<alphabetId, Map<lettre, {count, lastUsed}>>
    this.usageStats = new Map();
    
    // Configuration
    this.maxCacheSize = 50; // Maximum 50 lettres en cache (~68 MB)
    this.cleanupInterval = 5 * 60 * 1000; // 5 minutes
    this.cleanupTimer = null;
    
    // Lettres fréquentes par langue (pour préchargement)
    this.frequentLetters = {
      fr: ['E', 'A', 'R', 'S', 'T', 'N', 'I', 'O', 'L', 'U'],
      en: ['E', 'T', 'A', 'O', 'I', 'N', 'S', 'H', 'R', 'D'],
      // Par défaut, utiliser les lettres françaises
      default: ['E', 'A', 'R', 'S', 'T', 'N', 'I', 'O', 'L', 'U']
    };
  }

  /**
   * Initialise le cache pour un alphabet
   */
  initAlphabet(alphabetId) {
    if (!this.cache.has(alphabetId)) {
      this.cache.set(alphabetId, new Map());
      this.thumbnailCache.set(alphabetId, new Map());
      this.preloadedLetters.set(alphabetId, new Set());
      this.usageStats.set(alphabetId, new Map());
    }
  }

  /**
   * Génère un thumbnail (première frame) depuis une vidéo
   * Utilise l'événement 'seeked' pour s'assurer que la première frame est décodée
   * (évite le frame noir avec loadeddata seul)
   */
  generateThumbnail(video) {
    return new Promise((resolve, reject) => {
      let resolved = false;
      const videoUrl = video.src || video.currentSrc || 'unknown';
      
      // Vérifier si les dimensions sont disponibles avant de commencer
      // Si les dimensions sont 0x0, cela indique des restrictions de sécurité
      // et le canvas sera vide de toute façon
      if (video.videoWidth === 0 && video.videoHeight === 0 && video.readyState >= 1) {
        // Métadonnées chargées mais dimensions toujours 0 = restrictions de sécurité
        reject(new Error('Canvas empty - security restrictions prevent thumbnail generation'));
        return;
      }
      
      const drawFirstFrame = () => {
        if (resolved) return;
        resolved = true;
        
        try {
          // Vérifier les dimensions - maintenant qu'on charge sans crossOrigin, elles devraient être disponibles
          const width = video.videoWidth || 0;
          const height = video.videoHeight || 0;
          
          const canvas = document.createElement('canvas');
          canvas.width = 200;
          canvas.height = 200;
          const ctx = canvas.getContext('2d');
          
          // Dessiner la vidéo sur le canvas
          try {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          } catch (drawError) {
            throw drawError;
          }
          
          // Vérifier si le canvas est vide (peut arriver avec des restrictions de sécurité)
          const imageData = ctx.getImageData(0, 0, Math.min(canvas.width, 10), Math.min(canvas.height, 10));
          const isEmpty = imageData.data.every((val, idx) => idx % 4 === 3 || val === 0); // Vérifier si tous les pixels sont transparents/noirs
          
          // Essayer de convertir en DataURL
          // Si cela échoue à cause de CORS (canvas tainted), on rejette mais ce n'est pas critique
          // car on peut toujours afficher la vidéo directement
          try {
            const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.85);
            
            // Si le canvas est vide, cela indique un problème de sécurité (restrictions d'entreprise)
            // Dans ce cas, on rejette pour que le composant utilise directement la vidéo
            if (isEmpty) {
              // Rejeter pour indiquer qu'on ne peut pas générer de thumbnail
              // Le composant utilisera directement la vidéo
              reject(new Error('Canvas empty - security restrictions prevent thumbnail generation'));
              return;
            }
            
            resolve(thumbnailDataUrl);
          } catch (corsError) {
            // Canvas tainted par CORS - cela peut arriver même sans crossOrigin si la vidéo vient d'un autre domaine
            // Dans ce cas, on rejette mais ce n'est pas une erreur fatale
            reject(new Error('Canvas tainted - impossible de générer le thumbnail (CORS)'));
          }
        } catch (error) {
          reject(new Error('Impossible de générer le thumbnail: ' + error.message));
        }
      };

      // S'assurer que la première frame est décodée (évite le frame noir)
      // Si la vidéo est déjà prête (readyState >= 2), générer le thumbnail immédiatement
      let timeoutId;
      const onSeeked = () => {
        clearTimeout(timeoutId);
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('error', onError);
        drawFirstFrame();
      };
      const onError = (e) => {
        clearTimeout(timeoutId);
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('error', onError);
        if (!resolved) {
          resolved = true;
          reject(e);
        }
      };

      video.addEventListener('seeked', onSeeked);
      video.addEventListener('error', onError);

      // Vérifier si la vidéo est déjà prête
      if (video.readyState >= 2) {
        // Vidéo prête, essayer de seeker immédiatement
        video.currentTime = 0.001;
      } else {
        // Attendre que la vidéo soit prête
        const onCanPlay = () => {
          video.removeEventListener('canplay', onCanPlay);
          video.currentTime = 0.001;
        };
        video.addEventListener('canplay', onCanPlay, { once: true });
      }

      // Timeout de sécurité si seeked ne se déclenche pas (réduit à 500ms pour être plus rapide)
      timeoutId = setTimeout(() => {
        if (!resolved && video.readyState >= 2) {
          video.removeEventListener('seeked', onSeeked);
          video.removeEventListener('error', onError);
          drawFirstFrame();
        }
      }, 500);
    });
  }

  /**
   * Récupère une animation depuis le cache ou la charge
   */
  async getAnimation(letter, alphabetId, videoUrl) {
    this.initAlphabet(alphabetId);

    const alphabetCache = this.cache.get(alphabetId);
    const upperLetter = letter.toUpperCase();

    // Vérifier si déjà en cache
    if (alphabetCache.has(upperLetter)) {
      this.updateUsage(alphabetId, upperLetter);
      const cachedVideo = alphabetCache.get(upperLetter);
      // Ne plus générer ni stocker de première image ici :
      // on se contente de réutiliser la vidéo déjà en mémoire.
      return cachedVideo;
    }

    // Charger la vidéo (sans génération de thumbnail)
    const videoElement = await this.loadVideo(videoUrl);

    // Ajouter au cache (vidéo seulement)
    this.addToCache(alphabetId, upperLetter, videoElement);

    return videoElement;
  }

  /**
   * Récupère le thumbnail d'une lettre depuis le cache
   */
  getThumbnail(letter, alphabetId) {
    // Nouvelle logique : on ne stocke plus les premières images (thumbnails)
    // dans le code / cache. Toujours retourner null pour forcer l'usage direct
    // des vidéos, y compris pour l'affichage de la première frame.
    return null;
  }

  /**
   * Charge une vidéo depuis une URL
   * Pour les thumbnails, on charge sans crossOrigin pour obtenir les dimensions
   * Pour l'export, on utilisera crossOrigin dans le composant vidéo lui-même
   */
  loadVideo(url) {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      // Ne pas utiliser crossOrigin ici pour permettre l'accès aux dimensions
      // crossOrigin sera utilisé uniquement dans le composant vidéo pour l'export si nécessaire
      
      const onLoadedMetadata = () => {
      };
      
      const onLoadedData = () => {
        video.removeEventListener('loadedmetadata', onLoadedMetadata);
        video.removeEventListener('loadeddata', onLoadedData);
        video.removeEventListener('error', onError);
        // Résoudre dès que les données sont chargées
        resolve(video);
      };
      
      const onError = (e) => {
        video.removeEventListener('loadedmetadata', onLoadedMetadata);
        video.removeEventListener('loadeddata', onLoadedData);
        video.removeEventListener('error', onError);
        reject(new Error(`Erreur de chargement de la vidéo: ${url}`));
      };
      
      video.addEventListener('loadedmetadata', onLoadedMetadata);
      video.addEventListener('loadeddata', onLoadedData);
      video.addEventListener('error', onError);
      
      video.src = url;
    });
  }

  /**
   * Ajoute une vidéo au cache
   */
  addToCache(alphabetId, letter, videoElement) {
    const alphabetCache = this.cache.get(alphabetId);
    
    // Vérifier la limite de cache
    if (alphabetCache.size >= this.maxCacheSize) {
      this.evictLRU(alphabetId);
    }
    
    alphabetCache.set(letter, videoElement);
    this.updateUsage(alphabetId, letter);
  }

  /**
   * Évince la lettre la moins récemment utilisée
   */
  evictLRU(alphabetId) {
    const alphabetCache = this.cache.get(alphabetId);
    const stats = this.usageStats.get(alphabetId);
    const preloaded = this.preloadedLetters.get(alphabetId);
    
    // Ne pas évincer les lettres préchargées
    let candidates = Array.from(alphabetCache.keys()).filter(
      letter => !preloaded.has(letter)
    );
    
    if (candidates.length === 0) {
      // Si toutes sont préchargées, évincer quand même la moins utilisée
      candidates = Array.from(alphabetCache.keys());
    }
    
    // Trouver la lettre la moins utilisée
    let lruLetter = candidates[0];
    let minUsage = stats.get(lruLetter)?.count || 0;
    let minLastUsed = stats.get(lruLetter)?.lastUsed || Date.now();
    
    for (const letter of candidates) {
      const stat = stats.get(letter);
      if (!stat) {
        lruLetter = letter;
        break;
      }
      
      if (stat.count < minUsage || 
          (stat.count === minUsage && stat.lastUsed < minLastUsed)) {
        lruLetter = letter;
        minUsage = stat.count;
        minLastUsed = stat.lastUsed;
      }
    }
    
    // Supprimer du cache
    const video = alphabetCache.get(lruLetter);
    if (video) {
      video.src = ''; // Libérer la mémoire
      video.load();
    }
    alphabetCache.delete(lruLetter);
    stats.delete(lruLetter);
  }

  /**
   * Met à jour les statistiques d'utilisation
   */
  updateUsage(alphabetId, letter) {
    const stats = this.usageStats.get(alphabetId);
    const current = stats.get(letter) || { count: 0, lastUsed: Date.now() };
    stats.set(letter, {
      count: current.count + 1,
      lastUsed: Date.now()
    });
  }

  /**
   * Précharge les lettres fréquentes d'un alphabet
   */
  async preloadLetters(letters, alphabetId, videoUrls, onProgress) {
    this.initAlphabet(alphabetId);

    const preloaded = this.preloadedLetters.get(alphabetId);
    const total = letters.length;
    let loaded = 0;
    
    // Filtrer les lettres déjà préchargées
    const toLoad = letters.filter((letter, index) => {
      const upperLetter = letter.toUpperCase();
      if (preloaded.has(upperLetter)) {
        loaded++;
        if (onProgress) {
          onProgress(loaded, total, Math.round((loaded / total) * 100));
        }
        return false;
      }
      return true;
    });
    

    // Charger les lettres restantes
    for (let i = 0; i < toLoad.length; i++) {
      const letter = toLoad[i];
      const upperLetter = letter.toUpperCase();
      const videoUrl = videoUrls[letter] || videoUrls[upperLetter];

      if (!videoUrl) {
        console.warn(`URL manquante pour la lettre ${letter}`);
        continue;
      }

      try {
        const videoElement = await this.loadVideo(videoUrl);

        // Ne plus générer ni stocker de thumbnail ici.
        // On garde uniquement la vidéo en cache pour un déclenchement immédiat.
        this.addToCache(alphabetId, upperLetter, videoElement);
        preloaded.add(upperLetter);

        loaded++;
        if (onProgress) {
          onProgress(loaded, total, Math.round((loaded / total) * 100));
        }
      } catch (error) {
        console.error(`Erreur lors du préchargement de ${letter}:`, error);
      }
    }
  }

  /**
   * Change d'alphabet (vide le cache de l'ancien si nécessaire)
   */
  switchAlphabet(newAlphabetId) {
    // Pour l'instant, on garde tous les alphabets en cache
    // On pourrait implémenter une logique pour limiter le nombre d'alphabets
    this.initAlphabet(newAlphabetId);
  }

  /**
   * Nettoie les vidéos non utilisées depuis un certain temps
   */
  clearUnused(maxAge = 5 * 60 * 1000) {
    const now = Date.now();
    
    for (const [alphabetId, stats] of this.usageStats.entries()) {
      const alphabetCache = this.cache.get(alphabetId);
      const preloaded = this.preloadedLetters.get(alphabetId);
      
      for (const [letter, stat] of stats.entries()) {
        // Ne pas nettoyer les lettres préchargées
        if (preloaded.has(letter)) {
          continue;
        }
        
        if (now - stat.lastUsed > maxAge) {
          const video = alphabetCache.get(letter);
          if (video) {
            video.src = '';
            video.load();
          }
          alphabetCache.delete(letter);
          stats.delete(letter);
          
          // Supprimer aussi le thumbnail
          const thumbnailCache = this.thumbnailCache.get(alphabetId);
          if (thumbnailCache) {
            thumbnailCache.delete(letter);
          }
        }
      }
    }
  }

  /**
   * Vide complètement le cache d'un alphabet
   */
  clearCache(alphabetId) {
    const alphabetCache = this.cache.get(alphabetId);
    if (alphabetCache) {
      for (const video of alphabetCache.values()) {
        video.src = '';
        video.load();
      }
      alphabetCache.clear();
    }
    
    // Nettoyer aussi le cache de thumbnails
    const thumbnailCache = this.thumbnailCache.get(alphabetId);
    if (thumbnailCache) {
      thumbnailCache.clear();
    }
    
    this.preloadedLetters.delete(alphabetId);
    this.usageStats.delete(alphabetId);
    this.cache.delete(alphabetId);
    this.thumbnailCache.delete(alphabetId);
  }

  /**
   * Récupère les statistiques du cache
   */
  getCacheStats(alphabetId) {
    const alphabetCache = this.cache.get(alphabetId) || new Map();
    const preloaded = this.preloadedLetters.get(alphabetId) || new Set();
    
    return {
      size: alphabetCache.size,
      preloaded: preloaded.size,
      maxSize: this.maxCacheSize
    };
  }

  /**
   * Démarre le nettoyage automatique
   */
  startCleanup() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    
    this.cleanupTimer = setInterval(() => {
      this.clearUnused();
    }, this.cleanupInterval);
  }

  /**
   * Arrête le nettoyage automatique
   */
  stopCleanup() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }
}

// Instance singleton
const animationCache = new AnimationCache();

// Démarrer le nettoyage automatique
animationCache.startCleanup();

export default animationCache;
